import { withFocusRing } from "@components/Accessibility/FocusRing/FocusRing";
import { StyledButtonLink } from "@components/Styled/Button";
import { clsx } from "clsx";
import { Link } from "gatsby";
import React from "react";
import SwiperCore, {
  A11y,
  Autoplay,
  Keyboard,
  Mousewheel,
  Navigation,
  Scrollbar,
} from "swiper";
import { SwiperSlide } from "swiper/react";
import FeaturedCollectionCard from "./FeaturedCollectionCard";
import { withFeaturedCollectionCarousel } from "./withFeaturedCollectionCarousel";

import { IconVintageArrow } from "@bared/ui";

import Swiper from "@components/Swiper/Swiper";

SwiperCore.use([Autoplay, A11y, Keyboard, Mousewheel, Navigation, Scrollbar]);

type StyledArrowProps = {
  id: string;
  direction: string;
  count: number;
};

const Wrapper = withFocusRing((props: any) => (
  <div
    {...props}
    className={clsx(
      props.className,
      props.autoDelay && "[&_.swiper-wrapper]:!ease-linear",
    )}
  />
));

const Desktop = (props: any) => (
  <div
    {...props}
    className={clsx(
      props.className,
      "hidden justify-end pr-midi md:flex xl:pr-tera",
    )}
  />
);

const Mobile = (props: any) => (
  <div
    {...props}
    className={clsx(props.className, "mt-[18px] block md:hidden")}
  />
);

const StyledLink = withFocusRing((props: any) => (
  <Link
    {...props}
    className={clsx(
      props.className,
      "text-body leading-caption underline hover:text-secondary",
    )}
  />
));

const Button = (props: any) => (
  <StyledButtonLink
    {...props}
    className={clsx(props.className, "mx-midi mt-midi")}
  />
);

const ArrowWrapper = (props: any) => (
  <div
    {...props}
    className={clsx(props.className, "ml-midi flex items-center md:ml-mini")}
  />
);

const StyledArrow = withFocusRing((props: any) => (
  <div
    {...props}
    className={clsx(
      props.className,
      "!inline-flex cursor-pointer text-primary hover:opacity-70",
      props.direction === "prev" && "mr-mini rotate-180 transform",
    )}
  />
));

const StyledSwiper = (props: any) => (
  <Swiper
    {...props}
    className={clsx(
      props.className,
      "mt-maxi [&_.group]:focus-visible:outline-dashed [&_.group]:focus-visible:outline-1 [&_.group]:focus-visible:outline-offset-[-1px] [&_.group]:focus-visible:outline-current [&_.swiper-initialized]:mt-maxi [&_.swiper-initialized]:pl-midi [&_.swiper-initialized]:xl:pl-tera [&_.swiper-slide]:focus-visible:outline-dashed [&_.swiper-slide]:focus-visible:outline-1 [&_.swiper-slide]:focus-visible:outline-offset-[-1px] [&_.swiper-slide]:focus-visible:outline-current",
    )}
  />
);

const SliderArrow = ({ id, direction, count }: StyledArrowProps) => (
  <StyledArrow id={`${direction}-${id}`} direction={direction} count={count}>
    <IconVintageArrow className="w-[20px]" />
  </StyledArrow>
);

export const FeaturedCollectionCarousel = withFeaturedCollectionCarousel(
  ({
    activeTab,
    autoDelay,
    collectionContents,
    changeDirection,
    isMD,
    _key,
    setSwiper,
  }) => (
    <Wrapper
      autoDelay={!!autoDelay}
      role="tabpanel"
      aria-labelledby={`${activeTab?.toLowerCase()}_${_key}`}
    >
      {collectionContents?.map(({ link, contents }) => (
        <>
          <Desktop>
            {link?.url && (
              <StyledLink to={link?.url} tabIndex={0}>
                {link?.title}
              </StyledLink>
            )}

            {!autoDelay ? (
              <ArrowWrapper>
                <SliderArrow
                  id={`featured-collections-${_key}`}
                  direction="prev"
                  count={contents?.length}
                />
                <SliderArrow
                  id={`featured-collections-${_key}`}
                  direction="next"
                  count={contents?.length}
                />
              </ArrowWrapper>
            ) : null}
          </Desktop>

          <StyledSwiper
            speed={autoDelay && autoDelay !== "0" && Number(autoDelay)}
            loop={autoDelay ? true : false}
            autoplay={
              autoDelay
                ? {
                    delay: 0,
                    disableOnInteraction: false,
                  }
                : { enabled: false }
            }
            onSwiper={setSwiper}
            onSlideChange={changeDirection}
            navigation={
              !autoDelay
                ? {
                    prevEl: `#prev-featured-collections-${_key}`,
                    nextEl: `#next-featured-collections-${_key}`,
                  }
                : { nextEl: null, prevEl: null }
            }
            keyboard={{
              enabled: true,
            }}
            mousewheel={{
              enabled: true,
              forceToAxis: true,
            }}
            a11y={{
              enabled: true,
              prevSlideMessage: "Previous slide",
              nextSlideMessage: "Next slide",
              firstSlideMessage: "This is the first slide",
              lastSlideMessage: "This is the last slide",
              slideRole: "group",
            }}
            key={contents?.length}
            scrollbar={!isMD && { draggable: true }}
            slidesPerView={1.5}
            spaceBetween={0}
            lazy={true}
            preloadImages={false}
            observer={true}
            observeParents={true}
            watchOverflow={true}
            breakpoints={{
              415: {
                slidesPerView: 2.5,
                spaceBetween: 0,
                observer: true,
                observeParents: true,
              },
              769: {
                slidesPerView: 3.5,
                spaceBetween: 0,
                observer: true,
                observeParents: true,
              },
              1025: {
                slidesPerView: 4.5,
                spaceBetween: 0,
                observer: true,
                observeParents: true,
              },
            }}
          >
            {contents?.map((content) => (
              <SwiperSlide key={content?._key} tabIndex={0}>
                <FeaturedCollectionCard
                  image={content?.image}
                  link={content?.link}
                />
              </SwiperSlide>
            ))}
          </StyledSwiper>

          <Mobile>
            {link?.url && (
              <Button
                height="56"
                textSize="16"
                colour="transparent"
                border
                to={link?.url}
              >
                {link?.title}
              </Button>
            )}
          </Mobile>
        </>
      ))}
    </Wrapper>
  ),
);
